import {
  addItemToCollection,
  getItemFromCollection,
  updateItemInCollection,
} from "directus/src/operations";

import { getRequiredFields } from "components/form/utils";
import { jobEmployeeSchema } from "./schema";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { getUserFields } from "components/user/config";
import { invalidateAllQueries } from "utils/utils";
import { toast } from "sonner";

const FIELDS = [
  "id",
  "is_logging_restricted",
  "is_shadow",
  getUserFields("employee"),
  getUserFields("reviewer"),
];

export function useJobEmployeeForm({ jobId,  onSuccess }) {
  

  const form = useForm({
    resolver: zodResolver(jobEmployeeSchema),
    defaultValues: {
      employee: null,
      reviewer: null,
      is_logging_restricted: false,
      is_shadow: false,
    },
  });

  const requiredFields = useMemo(() => getRequiredFields(jobEmployeeSchema), []);

  async function handleCreate(context) {
    const values = context.values;
    try {
      await addItemToCollection("jobs_employees", {
        job: jobId,
        employee: values.employee?.id,
        reviewer: values.reviewer?.id ?? null,
        is_logging_restricted: values.is_logging_restricted,
        is_shadow: values.is_shadow,
      });
      invalidateAllQueries();
      onSuccess?.();
    } catch (error) {
      toast.error(error?.errors?.[0]?.message ?? error?.message ?? "Failed to add employee");
    }
  }

  async function handleRead(id) {
    const response = await getItemFromCollection("jobs_employees", id, {
      fields: FIELDS,
    });

    return {
      employee: response.employee ?? null,
      reviewer: response.reviewer ?? null,
      is_logging_restricted: response.is_logging_restricted ?? false,
      is_shadow: response.is_shadow ?? false,
    };
  }

  async function handleUpdate(id, context) {
    const values = context.dirtyValues;

    const payload = {};
    if ("employee" in values) payload.employee = values.employee?.id;
    if ("reviewer" in values) payload.reviewer = values.reviewer?.id ?? null;
    if ("is_logging_restricted" in values)
      payload.is_logging_restricted = values.is_logging_restricted;
    if ("is_shadow" in values) payload.is_shadow = values.is_shadow;

    try {
      await updateItemInCollection("jobs_employees", id, payload);
      invalidateAllQueries();
      onSuccess?.();
    } catch (error) {
      toast.error(error?.errors?.[0]?.message ?? error?.message ?? "Failed to update employee");
    }
  }

  return {
    form,
    requiredFields,
    handleCreate,
    handleRead,
    handleUpdate,
  };
}
